/* eslint-disable react-hooks/exhaustive-deps */
import { Box, ButtonProps, useMediaQuery, useTheme } from "@mui/material";
import { GridAddIcon, GridDeleteIcon } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { COLORS } from "../../assets/theme";
import Actions from "../../components/common/Actions";
import CustomDateRangePicker from "../../components/common/CustomDateRangePicker";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import ConditionValuesField from "../../components/ConditionValuesField";
import ToastNotification from "../../components/ToastNotification";
import { fetchCountry, useCountriesQuery } from "../../services/CountryService";
import {
  addFallBackRoutingApi,
  ConditionValueType,
  deleteFallBackRoutingApi,
  editFallBackRoutingApi,
  exportFallBackRouting,
  FallBackRouting,
  FallBackRoutingCreateAllApi,
  fetchFallBackRoutingHistory,
  fetchSmppStatusCodes,
  getFallBackRoutingById,
  useFallBackRoutingQsuery,
} from "../../services/FallBackRoutingService";
import { fetchOperator } from "../../services/OperatorsService";
import {
  fetchProvider,
  useProviderOptionQuery,
} from "../../services/ProviderService";
import {
  fetchRoutingPlan,
  useRoutingPlansQuery,
} from "../../services/RoutingPlanService";
import {
  DateRange,
  IField,
  Option,
  Params,
} from "../../types/table/interfaces.type";
import { loadOperatorsByCountry } from "../../utils/commonDataUtils";

import History from "../../components/History";
import RelatedInstructionsDialog from "../../components/RelatedInstructionsDialog";
import AutoComplete, {
  IAutoCompleteProps,
} from "../../components/common/AutoComplete";
import { hasJwtPermission } from "../../services/ServicesHelpers";

const DLR_STATUS_LABELS: Record<string, string> = {
  ENROUTE: "ENROUTE",
  DELIVRD: "DELIVERED",
  EXPIRED: "EXPIRED",
  DELETED: "DELETED",
  UNDELIV: "UNDELIVERABLE",
  ACCEPTD: "ACCEPTED",
  UNKNOWN: "UNKNOWN",
  REJECTD: "REJECTED",
};

const DLR_STATUS_VALUES = new Set(Object.keys(DLR_STATUS_LABELS));

const CONDITION_LABEL_OPTIONS = [
  { label: "DLR Status", value: "DLR_STATUS" },
  { label: "Command Status", value: "COMMAND_STATUS" },
  { label: "DLR Error Code", value: "DLR_ERROR_CODE" },
];

// Condition types whose value is a code sent in its own backend field instead
// of `status`.
const CODE_CONDITION_FIELDS = {
  COMMAND_STATUS: "commandStatus",
  DLR_ERROR_CODE: "dlrErrorCode",
} as const;

type CodeConditionType = keyof typeof CODE_CONDITION_FIELDS;

const isCodeConditionType = (type?: string): type is CodeConditionType =>
  !!type && type in CODE_CONDITION_FIELDS;

const CODE_CONDITION_PREFIXES: Record<CodeConditionType, string> = {
  COMMAND_STATUS: "Command Status",
  DLR_ERROR_CODE: "DLR Error Code",
};

const getCodeConditionType = (
  condition: ConditionValueType,
): CodeConditionType | undefined =>
  (Object.keys(CODE_CONDITION_FIELDS) as CodeConditionType[]).find(
    (type) => condition[CODE_CONDITION_FIELDS[type]] != null,
  );

const DLR_ERROR_CODE_LENGTH = 3;

const DLR_ERROR_CODE_TOOLTIP = `The DLR error code must be exactly ${DLR_ERROR_CODE_LENGTH} characters (e.g. 000).`;

const isDlrErrorCodeInvalid = (value?: string | null) =>
  (value ?? "").length !== DLR_ERROR_CODE_LENGTH;

const DLR_VALUE_OPTIONS = [
  { label: "ENROUTE", value: "ENROUTE" },
  { label: "DELIVERED", value: "DELIVRD" },
  { label: "EXPIRED", value: "EXPIRED" },
  { label: "DELETED", value: "DELETED" },
  { label: "UNDELIVERABLE", value: "UNDELIV" },
  { label: "ACCEPTED", value: "ACCEPTD" },
  { label: "UNKNOWN", value: "UNKNOWN" },
  { label: "REJECTED", value: "REJECTD" },
];

const inferConditionLabelType = (conditions: ConditionValueType[]): string => {
  if (!conditions.length) return "DLR_STATUS";
  const first = conditions[0];
  const codeType = getCodeConditionType(first);
  if (codeType) return codeType;
  if (first.filterType) return first.filterType;
  if (first.status && DLR_STATUS_VALUES.has(first.status)) return "DLR_STATUS";
  return "DLR_STATUS";
};

const getDlrStatusLabel = (status?: string | null) => {
  if (!status) {
    return "Unknown status";
  }

  return DLR_STATUS_LABELS[status] || status;
};

const formatConditionStatus = (condition: ConditionValueType) => {
  const codeType = getCodeConditionType(condition);

  const valueLabel = codeType
    ? String(condition[CODE_CONDITION_FIELDS[codeType]])
    : getDlrStatusLabel(condition.status ?? undefined);

  const prefix = codeType ? CODE_CONDITION_PREFIXES[codeType] : "DLR";

  if (condition.conditionType === "EQUAL") {
    return `${prefix} equals ${valueLabel}`;
  }

  if (condition.conditionType === "NOT_EQUAL") {
    return `${prefix} not equals ${valueLabel}`;
  }

  if (condition.conditionType && valueLabel) {
    return `${condition.conditionType}: ${valueLabel}`;
  }

  return condition.conditionType || valueLabel;
};

const formatConditionStatuses = (conditions?: ConditionValueType[]) => {
  if (!conditions || conditions.length === 0) {
    return "No Conditions";
  }

  return conditions.map(formatConditionStatus).join(", ");
};

const FallBackRoutingPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [appliedFilters, setAppliedFilters] = useState<
    Params<FallBackRouting & DateRange>
  >({
    search: "",
  });
  const [filters, setFilters] = useState<
    Params<FallBackRouting & { countryId: number }>
  >({});
  const { data: routingPlansOptions = [] } = useRoutingPlansQuery();
  const { data: providersOptions = [] } = useProviderOptionQuery();
  const { data: countriesOptions = [] } = useCountriesQuery();
  const routingPlanOtions = routingPlansOptions;
  const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
  const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  const dialogRef = useRef<FallBackRoutingDialogRef>(null);
  const [relatedRow, setRelatedRow] = useState<FallBackRouting | null>(null);

  const handleOpenDialog = (
    mode: "add" | "edit" | "delete" | "history",
    id?: string,
    isClone = false,
  ) => {
    dialogRef.current?.openDialog(mode, id, isClone);
  };

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedRowIds([]);
  }, [filters]);

  const onRowSelect = (row: FallBackRouting, checked: boolean) => {
    setSelectedRowIds((prevSelected) => {
      const newSelected = checked
        ? [...prevSelected, String(row.id)]
        : prevSelected.filter((id) => id !== String(row.id));
      return newSelected;
    });
  };

  const isRowSelected = (row: FallBackRouting) =>
    selectedRowIds.includes(String(row.id));

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      fromDate: fromDate,
      toDate: toDate,
      routingPlanId: filters.routingPlanId,
      operatorId: filters.operatorId,
      providerId: filters.providerId,
    }));
    setSelectedRowIds([]);
  };

  const actions = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
      },
      isDisabled: !hasJwtPermission("GET_FALL_BACK_ROUTING"),
    },
    onCloneAction: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id), true);
      },
      isDisabled: !hasJwtPermission("POST_FALL_BACK_ROUTING"),
    },
    onViewHistory: {
      handler: async (id: number | string) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_FALL_BACK_ROUTINGS_HISTORY"),
    },
    onCompareRelated: {
      tooltip: "Fall Back Routings on the same route",
      handler: (row: FallBackRouting) => setRelatedRow(row),
      isDisabled: (row: FallBackRouting) =>
        !hasJwtPermission("GET_ALL_FALL_BACK_ROUTINGS") ||
        !row?.routingPlanId ||
        !row?.operatorId,
    },
  });

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Routing Plan",
      value: filters.routingPlanId || "",
      options: routingPlanOtions,
      clearable: true,
      placeholder: "Select Routing Plan",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedPlan = routingPlanOtions.find(
          (option) => option.value === value,
        );
        setFilters((prev) => ({
          ...prev,
          routingPlanId: String(selectedPlan?.value ?? ""),
        }));
      },
    },
    {
      label: "Country",
      value: filters.countryId ?? "",
      options: countriesOptions,
      placeholder: "Select Country",

      clearable: true,
      onChange: async (value: string | number | (string | number)[]) => {
        const selectedCountry = countriesOptions.find(
          (option) => option.value === value,
        );

        setFilters((prev) => ({
          ...prev,
          countryId: Number(selectedCountry?.value),
          operatorId: undefined,
        }));

        if (selectedCountry) {
          try {
            setIsOperatorDisabled(true);
            const operators = await loadOperatorsByCountry(
              Number(selectedCountry.value),
            );
            setOperatorsByCountry(operators);
          } catch (error: any) {
            ToastNotification.error(error.message);

            setOperatorsByCountry([]);
          } finally {
            setIsOperatorDisabled(false);
          }
        }
      },
    },
    {
      label: "Operator",
      value: filters.operatorId ?? "",
      options: operatorsByCountry,
      disabled: isOperatorDisabled,

      clearable: true,
      placeholder: "Select Operator",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedOperator = operatorsByCountry.find(
          (option) => option.value === value,
        );

        setFilters((prev) => ({
          ...prev,
          operatorId: Number(selectedOperator?.value),
        }));
      },
    },

    {
      label: "Provider",
      value: filters.providerId ?? "",
      options: providersOptions,
      clearable: true,
      placeholder: "Select Provider",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedProvider = providersOptions.find(
          (option) => option.value === value,
        );
        setFilters((prev) => ({
          ...prev,
          providerId: String(selectedProvider?.value ?? ""),
        }));
      },
    },
  ];

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_FALL_BACK_ROUTINGS",
    },
  ];

  const fields = useMemo<IField<FallBackRouting>[]>(
    () => [
      {
        key: "name",
        header: "Name",
      },
      {
        key: "routingPlanId",
        header: "Routing Plan",
        transform: async (t) => {
          if (!t.routingPlanId) return "-";
          try {
            const rp = await queryClient.fetchQuery({
              queryKey: ["routingPlan", t.routingPlanId],
              queryFn: () => fetchRoutingPlan(t.routingPlanId),
            });
            return rp?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "originalProviderId",
        header: "Provider",
        transform: async (t) => {
          if (!t.originalProviderId) return "-";
          try {
            const p = await queryClient.fetchQuery({
              queryKey: ["provider", t.originalProviderId],
              queryFn: () => fetchProvider(t.originalProviderId),
            });
            return p?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "Country",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const op = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            if (!op?.countryId) return "-";
            const country = await queryClient.fetchQuery({
              queryKey: ["country", op.countryId],
              queryFn: () => fetchCountry(op.countryId),
            });
            return country?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "Operator",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const op = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            return op?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "conditionStatuses",
        header: "Condition",
        transform: async (t) => formatConditionStatuses(t.conditionStatuses),
      },
      {
        key: "providerId",
        header: "Fall Back Provider",
        transform: async (t) => {
          if (!t.providerId) return "-";
          try {
            const p = await queryClient.fetchQuery({
              queryKey: ["provider", t.providerId],
              queryFn: () => fetchProvider(t.providerId),
            });
            const providerName = p?.name ?? "-";
            const percentage = t.percentage0 ? ` (${t.percentage0}%)` : "";
            return `${providerName}${percentage}`;
          } catch {
            return "-";
          }
        },
      },
      { key: "createdDate", header: "Created Date" },
      { key: "createdBy", header: "User" },
    ],
    [queryClient],
  );

  const providerNameField = useCallback(
    async (providerId?: string) => {
      if (!providerId) return "-";
      try {
        const p = await queryClient.fetchQuery({
          queryKey: ["provider", providerId],
          queryFn: () => fetchProvider(providerId),
        });
        return p?.name ?? "-";
      } catch {
        return "-";
      }
    },
    [queryClient],
  );

  // Routing plan / country / operator are identical for every row of this view,
  // so only the fields that can actually differ are listed.
  const relatedFields = useMemo<IField<FallBackRouting>[]>(
    () => [
      { key: "name", header: "Name" },
      {
        key: "conditionStatuses",
        header: "Condition",
        transform: async (t) => formatConditionStatuses(t.conditionStatuses),
      },
      {
        key: "originalProviderId",
        header: "Provider",
        transform: async (t) => providerNameField(t.originalProviderId),
      },
      { key: "activeTimer", header: "Active Timer" },
      {
        key: "providerId",
        header: "Fall Back Provider",
        transform: async (t) => providerNameField(t.providerId),
      },
      {
        key: "provider1Id",
        header: "Provider 1",
        transform: async (t) => providerNameField(t.provider1Id),
      },
      { key: "percentage1", header: "Percentage 1" },
      {
        key: "provider2Id",
        header: "Provider 2",
        transform: async (t) => providerNameField(t.provider2Id),
      },
      { key: "percentage2", header: "Percentage 2" },
      {
        key: "provider3Id",
        header: "Provider 3",
        transform: async (t) => providerNameField(t.provider3Id),
      },
      { key: "percentage3", header: "Percentage 3" },
      { key: "createdDate", header: "Created Date" },
      { key: "createdBy", header: "User" },
    ],
    [providerNameField],
  );

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <CustomLabel
        text="Fall Back Routing"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        marginTop={1}
        marginBottom={1}
      />
      <Box sx={{ mb: 3 }}>
        <SearchForm
          dateComponent={
            <CustomDateRangePicker
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
            />
          }
          autoCompletes={autoCompletes}
          buttons={buttons}
          rpdid="RPD"
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="Fall Back Routing List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginTop={1}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search || ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <GridAddIcon />,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.iconColor,
              permission: "POST_FALL_BACK_ROUTING",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              variant: "outlined" as ButtonProps["variant"],
              startIcon: <GridDeleteIcon />,
              onClick: () => handleOpenDialog("delete"),
              textColor: COLORS.iconColor,
              permission: "DELETE_FALL_BACK_ROUTING",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,
              onClick: async () => {
                try {
                  await exportFallBackRouting(appliedFilters);
                  ToastNotification.success("Export completed successfully!");
                } catch (error: any) {
                  ToastNotification.error(
                    error.message || "Export failed. Please try again.",
                  );
                }
              },
              showExportProgress: true,
              textColor: COLORS.iconColor,
              label: "Export",
              permission: "GET_ALL_FALL_BACK_ROUTINGS_CSV",
            },
          ]}
        />

        <CustomTable<FallBackRouting>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useFallBackRoutingQsuery}
          actions={actions}
          checkboxSelect={true}
          onRowSelect={onRowSelect}
          isRowSelected={isRowSelected}
          sortableFields={["name", "createdDate", "createdBy"]}
        />
      </Box>
      <FallBackRoutingDialog
        ref={dialogRef}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries({ queryKey: ["fallBackRouting"] });
        }}
        selectedRowIds={selectedRowIds}
        onClearSelection={() => setSelectedRowIds([])}
        countriesOptions={countriesOptions}
        loadOperatorsByCountry={loadOperatorsByCountry}
        routingPlanOtions={routingPlanOtions}
        providersOptions={providersOptions}
        appliedFilters={appliedFilters}
      />
      <RelatedInstructionsDialog<FallBackRouting>
        open={!!relatedRow}
        onClose={() => setRelatedRow(null)}
        row={relatedRow}
        title="Fall Back Routings on the Same Route"
        fields={relatedFields}
        fetchFunc={useFallBackRoutingQsuery}
      />
    </Box>
  );
};

interface DialogData extends Omit<FallBackRouting, "operatorId"> {
  operatorId?: number | number[];
  countryId: number;
}

export interface FallBackRoutingDialogRef {
  openDialog: (
    mode: "add" | "edit" | "delete" | "history",
    id?: string,
    isClone?: boolean,
  ) => void;
}

interface FallBackRoutingDialogProps {
  onRefresh: () => void;
  countriesOptions: Option[];
  routingPlanOtions: Option[];
  providersOptions: Option[];
  selectedRowIds: string[];
  appliedFilters: Params<FallBackRouting>;
  onClearSelection: () => void;
  loadOperatorsByCountry: (countryId: number) => Promise<Option[]>;
}

const FallBackRoutingDialog = forwardRef<
  FallBackRoutingDialogRef,
  FallBackRoutingDialogProps
>(
  (
    {
      onRefresh,
      countriesOptions,
      loadOperatorsByCountry,
      routingPlanOtions,
      providersOptions,
      appliedFilters,
      selectedRowIds,
      onClearSelection,
    },
    ref,
  ) => {
    const queryClient = useQueryClient();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<
      "add" | "edit" | "delete" | "history"
    >("add");
    const [isCloneMode, setIsCloneMode] = useState(false);
    const [selectedId, setSelectedId] = useState<string | undefined>();
    const [dialogData, setDialogData] = useState<Partial<DialogData>>({});
    const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
    const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [conditionLabelType, setConditionLabelType] =
      useState<string>("DLR_STATUS");
    const [smppStatusOptions, setSmppStatusOptions] = useState<
      { label: string; value: string }[]
    >([]);

    useEffect(() => {
      fetchSmppStatusCodes()
        .then((codes) => {
          const options = Object.entries(codes)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([code, description]) => ({
              label: `${code} - ${description}`,
              value: code,
            }));
          setSmppStatusOptions(options);
        })
        .catch(() => {
          setSmppStatusOptions([]);
        });
    }, []);

    useImperativeHandle(ref, () => ({
      openDialog: (mode, id, isClone = false) => {
        setDialogMode(isClone ? "add" : mode);
        setSelectedId(isClone ? undefined : id);
        setIsCloneMode(isClone);
        setDialogOpen(true);
        openDialog(mode, id, isClone);
      },
    }));

    const handleCountryChange = async (
      value: string | number | (string | number)[],
    ) => {
      const selectedCountry = Array.isArray(value) ? value[0] : value;
      setDialogData((prev) => ({
        ...prev,
        countryId: Number(selectedCountry),
        operatorId: undefined,
      }));

      if (selectedCountry) {
        try {
          const countryId = Number(selectedCountry);
          if (isNaN(countryId)) {
            throw new Error("Invalid country ID");
          }

          setIsOperatorDisabled(false);
          const operators = await loadOperatorsByCountry(countryId);
          setOperatorsByCountry(operators);
        } catch (error: any) {
          ToastNotification.error(
            "Error fetching operators for selected country.",
          );
          setOperatorsByCountry([]);
        }
      } else {
        setOperatorsByCountry([]);
        setIsOperatorDisabled(true);
      }
    };

    const validateForm = () => {
      const errors = {
        routingPlanId: !dialogData.routingPlanId,
        operatorId: !dialogData.operatorId,
        name: !dialogData.name,
        conditionStatuses: !dialogData.conditionStatuses,
      };
      setIsSaveDisabled(Object.values(errors).some(Boolean));
    };

    useEffect(validateForm, [dialogData]);

    const stringErrors = Object.keys(errors).reduce<Record<string, string>>(
      (acc, key) => {
        acc[key] = errors[key] ? "This field is required" : "";
        return acc;
      },
      {},
    );

    const isDlrErrorCodeCondition = conditionLabelType === "DLR_ERROR_CODE";

    const hasInvalidDlrErrorCode =
      isDlrErrorCodeCondition &&
      (dialogData.conditionStatuses || []).some((c) =>
        isDlrErrorCodeInvalid(c.status),
      );

    // An empty row is not flagged red while the user has not typed yet, but it
    // still keeps Save disabled through hasInvalidDlrErrorCode.
    const conditionErrors = isDlrErrorCodeCondition
      ? (dialogData.conditionStatuses || []).reduce<Record<string, string>>(
          (acc, c, index) => {
            if (c.status && isDlrErrorCodeInvalid(c.status)) {
              acc[`conditionStatuses.${index}.status`] = DLR_ERROR_CODE_TOOLTIP;
            }
            return acc;
          },
          { ...stringErrors },
        )
      : stringErrors;

    const validateField = (field: string, value: string) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: !value,
      }));
    };

    useEffect(() => {
      const requiredFields = [
        "routingPlanId",
        "countryId",
        "operatorId",
        "providerId",
        "name",
        "conditionStatuses",
      ];

      const isAnyFieldEmpty = requiredFields.some((field) => {
        const value = dialogData[field as keyof typeof dialogData];
        if (Array.isArray(value)) return value.length === 0;
        return value === undefined || value === null || value === "";
      });

      setIsSaveDisabled(isAnyFieldEmpty);
    }, [dialogData]);

    const resetDialogData = () => {
      setDialogData({});
      setDialogOpen(false);
      setOperatorsByCountry([]);
      setIsSaveDisabled(true);
      setIsOperatorDisabled(true);
      setIsCloneMode(false);
    };

    const mapConditionsForBackend = (
      conditions: ConditionValueType[],
    ): ConditionValueType[] =>
      conditions.map(({ filterType, ...c }) => ({
        conditionType: c.conditionType,
        status: isCodeConditionType(filterType) ? null : c.status,
        commandStatus:
          filterType === "COMMAND_STATUS" ? Number(c.status) : null,
        dlrErrorCode: filterType === "DLR_ERROR_CODE" ? c.status : null,
      }));

    const openDialog = useCallback(
      async (
        mode: "add" | "edit" | "delete" | "history",
        id?: string,
        isClone = false,
      ) => {
        setDialogMode(isClone ? "add" : mode);

        try {
          if (mode === "edit" || isClone) {
            if (!id) {
              console.warn("ID is required to edit a FallBack Routing.");
              return;
            }
            const fallbackData = await getFallBackRoutingById(id);

            const rawConditions: ConditionValueType[] =
              fallbackData.conditionStatuses || [];
            const inferredType = inferConditionLabelType(rawConditions);
            setConditionLabelType(inferredType);

            const processedConditionStatuses = rawConditions.length
              ? rawConditions.map((condition: ConditionValueType) => {
                  const codeType = getCodeConditionType(condition);
                  return {
                    conditionType: condition.conditionType || "",
                    status: codeType
                      ? String(condition[CODE_CONDITION_FIELDS[codeType]])
                      : condition.status || "",
                    filterType:
                      codeType || condition.filterType || inferredType,
                    commandStatus: undefined,
                    dlrErrorCode: undefined,
                  };
                })
              : [{ conditionType: "", status: "", filterType: inferredType }];

            // A clone keeps every value of the source route, only the name has
            // to be re-entered because it must stay unique.
            let countryId: number | undefined;

            if (fallbackData.operatorId) {
              try {
                const { countryId: selectedCountry } = await fetchOperator(
                  fallbackData.operatorId,
                );

                if (selectedCountry) {
                  countryId = Number(selectedCountry);
                  setIsOperatorDisabled(true);
                  const operators = await loadOperatorsByCountry(countryId);
                  setOperatorsByCountry(operators);
                }
              } catch (error: any) {
                console.error(
                  "Error loading country or operators by operator ID:",
                  error,
                );
              } finally {
                setIsOperatorDisabled(false);
              }
            }

            const sourceOperatorId = Array.isArray(fallbackData.operatorId)
              ? fallbackData.operatorId[0]
              : fallbackData.operatorId;

            setDialogData({
              ...fallbackData,
              ...(isClone ? { id: undefined, name: "" } : {}),
              conditionStatuses: processedConditionStatuses,
              countryId: countryId ?? "",
              operatorId:
                isClone && sourceOperatorId != null
                  ? [Number(sourceOperatorId)]
                  : sourceOperatorId,
            });
          } else if (mode === "delete") {
            if (!id) {
              console.warn("ID is required to delete a FallBack Routing.");
              return;
            }

            setDialogData({ id });
          } else {
            setConditionLabelType("DLR_STATUS");
            setDialogData({
              name: "",
              conditionStatuses: [
                { conditionType: "", status: "", filterType: "DLR_STATUS" },
              ],
              effectiveDate: new Date(),
            });
          }
        } catch (error: any) {
          console.error(
            `Error fetching FallBack Routing data for ${mode} mode:`,
            error,
          );
        }
      },
      [
        setDialogMode,
        setDialogData,
        setIsOperatorDisabled,
        setOperatorsByCountry,
        setConditionLabelType,
      ],
    );

    const addFallBackRouting = async (data: Partial<DialogData>) => {
      try {
        const { operatorId, ...rest } = data;
        const filteredData = Object.entries(rest).reduce(
          (acc, [key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
              acc[key] = value;
            }
            return acc;
          },
          {} as Record<string, any>,
        );
        const commonData: Partial<FallBackRouting> = {
          ...filteredData,
          type: "FALL_BACK_ROUTING",
          isDeleted: false,
          conditionStatuses: mapConditionsForBackend(
            data.conditionStatuses ?? [
              {
                conditionType: "EQUAL",
                status: "EXPIRED",
                filterType: "DLR_STATUS",
              },
            ],
          ),
          effectiveDate: new Date(),
        };

        if (Array.isArray(operatorId) && operatorId.length >= 1) {
          const payloads: Partial<FallBackRouting>[] = operatorId.map(
            (id, index) => ({
              ...commonData,
              operatorId: id,
              name: data.name ? `${data.name}_${index + 1}` : undefined,
              routingPlanId: data.routingPlanId,
              providerId: data.providerId,
            }),
          );

          await FallBackRoutingCreateAllApi(payloads);
        }

        onRefresh();
        setDialogOpen(false);
        setConditionLabelType("DLR_STATUS");
        setDialogData({
          conditionStatuses: [
            { conditionType: "", status: "", filterType: "DLR_STATUS" },
          ],
        });
        ToastNotification.success("FallBack Routing added successfully!");
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const editFallBackRouting = async (data: Partial<DialogData>) => {
      try {
        const { operatorId, percentage0, ...rest } = data;
        const filteredData = Object.entries(rest).reduce(
          (acc, [key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
              acc[key] = value;
            }
            return acc;
          },
          {} as Record<string, any>,
        );

        const mappedConditions = data.conditionStatuses
          ? mapConditionsForBackend(data.conditionStatuses)
          : undefined;

        const id = data.id as string;
        if (!id) {
          ToastNotification.warning("FallBack Routing ID is missing.");
          return;
        }

        const payload: Partial<FallBackRouting> = {
          ...filteredData,
          ...(mappedConditions ? { conditionStatuses: mappedConditions } : {}),
          type: "FALL_BACK_ROUTING",
          isDeleted: false,
          operatorId: Array.isArray(operatorId) ? operatorId[0] : operatorId,
        };

        await editFallBackRoutingApi(id, payload);

        onRefresh();
        setDialogOpen(false);
        ToastNotification.success("FallBack Routing updated successfully!");
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const deleteFallBackRouting = async () => {
      try {
        await deleteFallBackRoutingApi(selectedRowIds);
        ToastNotification.success("FallBack Route deleted successfully!");
        onClearSelection();
        onRefresh();
        setDialogOpen(false);
      } catch {
        ToastNotification.error("Error deleting FallBack Route.");
      }
    };

    const fieldHistory = useMemo<
      IField<FallBackRouting & { historyTimestamp: Date }>[]
    >(
      () => [
        {
          key: "name",
          header: "Name",
        },
        {
          key: "routingPlanId",
          header: "Routing Plan",
          transform: async (t) => {
            if (!t.routingPlanId) return "-";
            try {
              const rp = await queryClient.fetchQuery({
                queryKey: ["routingPlan", t.routingPlanId],
                queryFn: () => fetchRoutingPlan(t.routingPlanId),
              });
              return rp?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "originalProviderId",
          header: "Provider",
          transform: async (t) => {
            if (!t.originalProviderId) return "-";
            try {
              const p = await queryClient.fetchQuery({
                queryKey: ["provider", t.originalProviderId],
                queryFn: () => fetchProvider(t.originalProviderId),
              });
              return p?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "operatorId",
          header: "Country",
          transform: async (t) => {
            if (!t.operatorId) return "-";
            try {
              const op = await queryClient.fetchQuery({
                queryKey: ["operator", t.operatorId],
                queryFn: () => fetchOperator(t.operatorId),
              });
              if (!op?.countryId) return "-";
              const country = await queryClient.fetchQuery({
                queryKey: ["country", op.countryId],
                queryFn: () => fetchCountry(op.countryId),
              });
              return country?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "operatorId",
          header: "Operator",
          transform: async (t) => {
            if (!t.operatorId) return "-";
            try {
              const op = await queryClient.fetchQuery({
                queryKey: ["operator", t.operatorId],
                queryFn: () => fetchOperator(t.operatorId),
              });
              return op?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "providerId",
          header: "Fall Back Provider",
          transform: async (t) => {
            if (!t.providerId) return "-";
            try {
              const p = await queryClient.fetchQuery({
                queryKey: ["provider", t.providerId],
                queryFn: () => fetchProvider(t.providerId),
              });
              const providerName = p?.name ?? "-";
              const percentage = t.percentage0 ? ` (${t.percentage0}%)` : "";
              return `${providerName}${percentage}`;
            } catch {
              return "-";
            }
          },
        },
        {
          key: "provider1Id",
          header: "Provider 1",
          transform: async (t) => {
            if (!t.provider1Id) return "-";
            try {
              const p = await queryClient.fetchQuery({
                queryKey: ["provider", t.provider1Id],
                queryFn: () => fetchProvider(t.provider1Id),
              });
              const providerName = p?.name ?? "-";
              const percentage = t.percentage1 ? ` (${t.percentage1}%)` : "";
              return `${providerName}${percentage}`;
            } catch {
              return "-";
            }
          },
        },
        {
          key: "provider2Id",
          header: "Provider 2",
          transform: async (t) => {
            if (!t.provider2Id) return "-";
            try {
              const p = await queryClient.fetchQuery({
                queryKey: ["provider", t.provider2Id],
                queryFn: () => fetchProvider(t.provider2Id),
              });
              const providerName = p?.name ?? "-";
              const percentage = t.percentage2 ? ` (${t.percentage2}%)` : "";
              return `${providerName}${percentage}`;
            } catch {
              return "-";
            }
          },
        },
        {
          key: "provider3Id",
          header: "Provider 3",
          transform: async (t) => {
            if (!t.provider3Id) return "-";
            try {
              const p = await queryClient.fetchQuery({
                queryKey: ["provider", t.provider3Id],
                queryFn: () => fetchProvider(t.provider3Id),
              });
              const providerName = p?.name ?? "-";
              const percentage = t.percentage3 ? ` (${t.percentage3}%)` : "";
              return `${providerName}${percentage}`;
            } catch {
              return "-";
            }
          },
        },
        {
          key: "conditionStatuses",
          header: "Condition",
          transform: async (t) => formatConditionStatuses(t.conditionStatuses),
        },
        { key: "createdDate", header: "Created Date" },
        { key: "createdBy", header: "User" },
        { key: "historyTimestamp", header: "Date Modified" },
        { key: "createdBy", header: "Created By" },
        { key: "updatedBy", header: "Updated By" },
      ],
      [queryClient],
    );

    return (
      <CustomDialog
        open={dialogOpen}
        onClose={resetDialogData}
        mode={dialogMode}
        title={
          dialogMode === "history"
            ? "History"
            : dialogMode === "delete"
              ? "Delete FallBack Routing"
              : dialogMode === "edit"
                ? "Edit FallBack Routing"
                : isCloneMode
                  ? "Clone FallBack Routing"
                  : "Add New FallBack Routing"
        }
        fields={
          dialogMode === "history"
            ? [
                {
                  type: "custom",
                  component: (
                    <History
                      filters={appliedFilters}
                      fields={fieldHistory}
                      fetchFunc={({ size = 10 }) =>
                        fetchFallBackRoutingHistory(String(selectedId), {
                          size,
                        }).then((result) => ({
                          ...result,
                          content: result.content.map((item: any) => ({
                            ...item,
                            historyTimestamp:
                              item.historyTimestamp ?? new Date(),
                          })),
                        }))
                      }
                    />
                  ),
                },
              ]
            : dialogMode === "delete"
              ? [
                  {
                    type: "custom",
                    component: (
                      <Box>
                        <p>Are you sure you want to delete this route?</p>
                      </Box>
                    ),
                  },
                ]
              : [
                  {
                    type: "text",
                    label: "FallBack Routing Name",
                    bgColor: COLORS.WhiteBackground,
                    highlightLabelStar: true,
                    value: dialogData.name || "",
                    validateOnChange: true,
                    onValidation: (isError) => {
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        name: isError,
                      }));
                    },
                    onChange: (value) => {
                      const trimmedValue = String(value).trimStart();
                      setDialogData((prev) => ({
                        ...prev,
                        name: trimmedValue,
                      }));
                    },
                    placeholder: "Session name",
                    error: !!errors.name,
                  },

                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Routing Plan"
                        value={dialogData.routingPlanId || ""}
                        onChange={(
                          value: string | number | (string | number)[],
                        ) => {
                          setDialogData((prev) => ({
                            ...prev,
                            routingPlanId: String(value),
                          }));
                          validateField("routingPlanId", String(value));
                        }}
                        clearable={true}
                        options={routingPlanOtions}
                        placeholder="Select Routing Plan"
                        highlightLabelStar={true}
                        error={!!errors.routingPlanId}
                        helperText={
                          errors.routingPlanId ? "Routing Plan is required" : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Provider"
                        value={dialogData.originalProviderId || ""}
                        onChange={(
                          value: string | number | (string | number)[],
                        ) => {
                          setDialogData((prev) => ({
                            ...prev,
                            originalProviderId: String(value),
                          }));
                          validateField("originalProviderId", String(value));
                        }}
                        clearable={true}
                        options={providersOptions}
                        placeholder="Select Provider"
                        highlightLabelStar={true}
                        error={!!errors.originalProviderId}
                        helperText={
                          errors.originalProviderId
                            ? "Provider is required"
                            : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Country"
                        value={dialogData.countryId || ""}
                        onChange={handleCountryChange}
                        clearable={true}
                        options={countriesOptions}
                        placeholder="Select Country"
                        highlightLabelStar={true}
                        error={!!errors.country}
                        helperText={errors.country ? "Country is required" : ""}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "custom",
                    component: (
                      <AutoComplete
                        label="Operator"
                        multiple={dialogMode === "add"}
                        selectAll={dialogMode === "add"}
                        hideChips={dialogMode === "add"}
                        value={
                          dialogMode === "add"
                            ? Array.isArray(dialogData.operatorId)
                              ? dialogData.operatorId.map(String)
                              : []
                            : dialogData.operatorId !== undefined &&
                                dialogData.operatorId !== null
                              ? String(
                                  Array.isArray(dialogData.operatorId)
                                    ? dialogData.operatorId[0]
                                    : dialogData.operatorId,
                                )
                              : ""
                        }
                        options={operatorsByCountry}
                        onChange={(
                          value: string | number | (string | number)[],
                        ) => {
                          if (dialogMode === "add") {
                            const valueArray: number[] = Array.isArray(value)
                              ? value.map((v) => Number(v))
                              : value
                                ? [Number(value)]
                                : [];

                            setDialogData((prev) => ({
                              ...prev,
                              operatorId: valueArray,
                            }));
                          } else {
                            setDialogData((prev) => ({
                              ...prev,
                              operatorId:
                                value !== "" && value !== undefined
                                  ? Number(
                                      Array.isArray(value) ? value[0] : value,
                                    )
                                  : undefined,
                            }));
                          }
                        }}
                        clearable={true}
                        disablePortal={false}
                        error={!!errors.operatorId}
                        placeholder="Select Operator"
                        highlightLabelStar={true}
                        helperText={
                          errors.operatorId ? "Operator is required" : ""
                        }
                        disabled={isOperatorDisabled}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "custom",
                    label: "Conditions",
                    highlightLabelStar: true,
                    component: (
                      <ConditionValuesField
                        type={isDlrErrorCodeCondition ? "text" : "dropdown"}
                        label="Condition Type"
                        fallback
                        valueTooltip={
                          isDlrErrorCodeCondition
                            ? DLR_ERROR_CODE_TOOLTIP
                            : undefined
                        }
                        valueMaxLength={
                          isDlrErrorCodeCondition
                            ? DLR_ERROR_CODE_LENGTH
                            : undefined
                        }
                        conditionTypeOptions={[
                          { label: "NOT_EQUAL", value: "NOT_EQUAL" },
                          { label: "EQUAL", value: "EQUAL" },
                        ]}
                        valueOptions={
                          isCodeConditionType(conditionLabelType)
                            ? smppStatusOptions
                            : DLR_VALUE_OPTIONS
                        }
                        globalLabelOptions={CONDITION_LABEL_OPTIONS}
                        globalLabelValue={conditionLabelType}
                        onGlobalLabelChange={(newType) => {
                          setConditionLabelType(newType);
                          setDialogData((prev) => ({
                            ...prev,
                            conditionStatuses: [
                              {
                                conditionType: "",
                                status: "",
                                filterType: newType,
                              },
                            ],
                          }));
                        }}
                        conditionStatuses={(
                          dialogData.conditionStatuses || []
                        ).map((c) => ({
                          ...c,
                          status: c.status ?? "",
                        }))}
                        handleConditionChange={(index, field, status) => {
                          setDialogData((prev) => {
                            const updatedConditions = [
                              ...(prev.conditionStatuses || []),
                            ];
                            updatedConditions[index] = {
                              ...updatedConditions[index],
                              [field]: status,
                            };
                            return {
                              ...prev,
                              conditionStatuses: updatedConditions,
                            };
                          });
                        }}
                        handleAddCondition={() => {
                          setDialogData((prev) => ({
                            ...prev,
                            conditionStatuses: [
                              ...(prev.conditionStatuses || []),
                              {
                                conditionType: "",
                                status: "",
                                filterType: conditionLabelType,
                              },
                            ],
                          }));
                        }}
                        handleRemoveCondition={(index) => {
                          setDialogData((prev) => {
                            const updatedConditions = [
                              ...(prev.conditionStatuses || []),
                            ];
                            updatedConditions.splice(index, 1);
                            return {
                              ...prev,
                              conditionStatuses: updatedConditions,
                            };
                          });
                        }}
                        errors={conditionErrors}
                      />
                    ),
                  },

                  {
                    type: "text",
                    label: "Active Timer",
                    bgColor: COLORS.WhiteBackground,
                    endAdornment: "sec",
                    value: dialogData.activeTimer ?? "",
                    placeholder: "Active Timer",
                    typeinput: "number",
                    onChange: (value: string | number) => {
                      const processedValue =
                        typeof value === "string"
                          ? value.trim()
                          : String(value);
                      setDialogData((prev) => ({
                        ...prev,
                        activeTimer:
                          processedValue === ""
                            ? undefined
                            : Math.min(Math.floor(Number(processedValue))),
                      }));
                    },
                  },
                  {
                    type: "custom",
                    label: "Fall Back Provider",
                    highlightLabelStar: true,
                    component: (
                      <AutoComplete
                        label="Fall Back Provider"
                        value={dialogData.providerId || ""}
                        onChange={(
                          value: string | number | (string | number)[],
                        ) => {
                          setDialogData((prev) => ({
                            ...prev,
                            providerId: String(value),
                          }));
                          validateField("providerId", String(value));
                        }}
                        clearable={true}
                        options={providersOptions}
                        placeholder="Select Fall Back Provider"
                        highlightLabelStar={true}
                        error={!!errors.providerId}
                        helperText={
                          errors.providerId ? "Provider is required" : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: COLORS.WhiteBackground,
                          },
                        }}
                      />
                    ),
                  },
                  {
                    type: "combined",
                    label: "Blending",
                    showLabel: true,
                    fields: [
                      {
                        type: "custom",
                        component: (
                          <AutoComplete
                            value={dialogData.provider1Id ?? ""}
                            options={providersOptions}
                            onChange={(
                              value: string | number | (string | number)[],
                            ) => {
                              setDialogData((prev) => ({
                                ...prev,
                                provider1Id: Array.isArray(value)
                                  ? value.join(",")
                                  : String(value),
                              }));
                            }}
                            clearable={true}
                            disablePortal={false}
                            error={!!errors.provider1Id}
                            placeholder="Provider 1"
                            highlightLabelStar={true}
                            helperText={
                              errors.provider1Id ? "Provider is required" : ""
                            }
                            width="auto"
                            sx={{
                              flex: 1,
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: COLORS.WhiteBackground,
                              },
                            }}
                          />
                        ),
                      },
                      {
                        type: "text",
                        value: dialogData.percentage1 || "",
                        placeholder: "Percentage 1",
                        bgColor: COLORS.WhiteBackground,
                        onChange: (value: string | number) => {
                          const numericValue = Number(value);
                          const cappedValue =
                            numericValue > 100 ? 100 : numericValue;
                          setDialogData((prev) => ({
                            ...prev,
                            percentage1: cappedValue,
                          }));
                        },
                        error: !!errors.percentage1,
                        helperText: errors.percentage1
                          ? "Percentage 1 is required and must be 100 or less"
                          : "",
                      },
                    ],
                    layout: "row",
                    sx: { gap: 3 },
                    highlightLabelStar: true,
                  },
                  {
                    type: "combined",
                    fields: [
                      {
                        type: "custom",
                        component: (
                          <AutoComplete
                            value={dialogData.provider2Id ?? ""}
                            options={providersOptions}
                            onChange={(
                              value: string | number | (string | number)[],
                            ) => {
                              setDialogData((prev) => ({
                                ...prev,
                                provider2Id: Array.isArray(value)
                                  ? value.join(",")
                                  : String(value),
                              }));
                            }}
                            clearable={true}
                            disablePortal={false}
                            error={!!errors.provider2Id}
                            placeholder="Provider 2"
                            highlightLabelStar={true}
                            helperText={
                              errors.provider2Id ? "Provider is required" : ""
                            }
                            width="auto"
                            sx={{
                              flex: 1,
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: COLORS.WhiteBackground,
                              },
                            }}
                          />
                        ),
                      },
                      {
                        type: "text",
                        value: dialogData.percentage2 || "",
                        placeholder: "Percentage 2",
                        bgColor: COLORS.WhiteBackground,
                        onChange: (value: string | number) => {
                          const numericValue = Number(value);
                          const cappedValue =
                            numericValue > 100 ? 100 : numericValue;
                          setDialogData((prev) => ({
                            ...prev,
                            percentage2: cappedValue,
                          }));
                        },
                        error: !!errors.percentage2,
                        helperText: errors.percentage2
                          ? "Percentage 2 is required"
                          : "",
                      },
                    ],
                    layout: "row",
                    sx: { gap: 3 },
                    highlightLabelStar: true,
                  },
                  {
                    type: "combined",
                    fields: [
                      {
                        type: "custom",
                        component: (
                          <AutoComplete
                            value={dialogData.provider3Id ?? ""}
                            options={providersOptions}
                            onChange={(
                              value: string | number | (string | number)[],
                            ) => {
                              setDialogData((prev) => ({
                                ...prev,
                                provider3Id: Array.isArray(value)
                                  ? value.join(",")
                                  : String(value),
                              }));
                            }}
                            clearable={true}
                            disablePortal={false}
                            error={!!errors.provider3Id}
                            placeholder="Provider 3"
                            highlightLabelStar={true}
                            helperText={
                              errors.provider3Id ? "Provider is required" : ""
                            }
                            width="auto"
                            sx={{
                              flex: 1,
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: COLORS.WhiteBackground,
                              },
                            }}
                          />
                        ),
                      },
                      {
                        type: "text",
                        value: dialogData.percentage3 || "",
                        placeholder: "Percentage 3",
                        bgColor: COLORS.WhiteBackground,
                        onChange: (value: string | number) => {
                          const numericValue = Number(value);
                          const cappedValue =
                            numericValue > 100 ? 100 : numericValue;
                          setDialogData((prev) => ({
                            ...prev,
                            percentage3: cappedValue,
                          }));
                        },
                        error: !!errors.percentage3,
                        helperText: errors.percentage3
                          ? "Percentage 3 is required"
                          : "",
                      },
                    ],
                    layout: "row",
                    sx: { gap: 3 },
                    highlightLabelStar: true,
                  },
                ]
        }
        buttons={
          dialogMode === "history"
            ? []
            : [
                dialogMode !== "delete"
                  ? {
                      label: "Save",
                      variant: "contained",
                      bgColor: COLORS.GOLD,
                      borderRadius: "12px",
                      onClick: async () => {
                        if (dialogMode === "edit") {
                          await editFallBackRouting(dialogData);
                        } else {
                          await addFallBackRouting(dialogData);
                        }
                      },
                      disabled:
                        isSaveDisabled ||
                        hasInvalidDlrErrorCode ||
                        (dialogMode === "edit"
                          ? !hasJwtPermission("PUT_FALL_BACK_ROUTING")
                          : !hasJwtPermission("POST_FALL_BACK_ROUTING")),
                    }
                  : {
                      label: "Delete",
                      variant: "contained",
                      bgColor: COLORS.GOLD,
                      textColor: COLORS.iconColor,
                      borderRadius: "12px",
                      onClick: deleteFallBackRouting,
                      disabled: !hasJwtPermission("DELETE_FALL_BACK_ROUTING"),
                    },
              ]
        }
      />
    );
  },
);

export default FallBackRoutingPage;
